import { COLORS } from '@btime/constants'
import { appendIf, capitalizeIfLower, formatDate } from '@btime/helpers'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { RefetchingIndicator } from '../../../../components/helpers/StatePresenter'
import { fromNow } from '../../../../utils/helpers/date'
import { getSemaphoreConfigColor } from '../../../../utils/helpers/semaphore'
import { getSlaStatusColor } from '../../../../utils/helpers/sla'
import { useConfig } from '../../../../containers/configs/_load/ConfigsLoader'
import { useOpenServiceOrderModal } from '../../../../utils/routes/modals/modalsActions'
import { useRowActionsColumn } from '../../../../components/tables/Table'
import { useServiceOrders } from '../_load/ServiceOrdersLoader'
import { useServiceOrdersActionsList } from '../../../../utils/actions/serviceOrders/list'
import ActionPlansTable from './ActionPlansTable'
import AssetPresenter from '../../../../containers/assets/AssetPresenter'
import Badge from '../../../../packages/kiti/components/presentation/Badge'
import DataTable from '../../../../packages/kiti/components/table/DataTable'
import EditableServiceOrdersTag from '../../../../containers/serviceOrders/EditableServiceOrdersTag'
import EditableServiceOrdersUser from '../../../../containers/serviceOrders/EditableServiceOrdersUser'
import IconCount from '../../../../components/shapes/IconCount'
import Link from '../../../../components/actions/Link'
import ListCounter from '../ListCounter'
import PlacePresenter from '../../../../containers/places/PlacePresenter'
import StatusTag, { PRIORITY } from '../../../../containers/serviceOrders/StatusTag'
import Text, { SmallText } from '../../../../packages/kiti/components/presentation/Text'
import moment from 'moment'

const STATUS_OPEN_AT = ['open', 'received', 'execution', 'pause', 'pending']

const ServiceOrdersTable = ({ customData, ...props }) => {
  const { t } = useTranslation()
  const semaphoreConfig = useConfig('service_order_started_since_colors')

  const onlySelfServiceOrders = useSessionPermission('ownedSOOnlyServiceOrder')
  const hidePlace = useSessionPermission('hideServiceOrdersPlace')
  const canViewServiceOrderInternalId = useSessionPermission('canViewServiceOrderInternalId')

  let data = useServiceOrders()
  const openDetailsModal = useOpenServiceOrderModal()
  data = customData || data

  const columns = [
    {
      label: t('listServiceOrders:table.code'),
      dataIndex: 'yearlyId',
      sortIndex: 'YEARLY_ID',
      flex: 4,
      headerSideContent: (
        <>
          {!onlySelfServiceOrders && <ListCounter />}
          <RefetchingIndicator />
        </>
      ),
      render: (value, record) => {
        const checklistName = capitalizeIfLower(record.checklist?.name)
        const name = record.name

        return (
          <Link onClick={() => openDetailsModal(record)} block>
            {canViewServiceOrderInternalId && (
              <SmallText size="xxsmall" color={COLORS.dark3}>{`#${record.id}`}</SmallText>
            )}
            <SmallText size="xsmall" color={COLORS.dark3}>
              {record.yearlyId}
            </SmallText>
            <Text size="h6" bold color={COLORS.dark2} breakWord>
              {name || checklistName}
            </Text>

            {!!name && (
              <SmallText size="xxsmall" color={COLORS.dark3} breakWord>
                {checklistName}
              </SmallText>
            )}
          </Link>
        )
      }
    },

    {
      label: '',
      dataIndex: 'childrenCount',
      width: 2.5,
      render: (value, record, index, { toggleExpand }) => (
        <IconCount value={value} onClick={toggleExpand} icon="BsListTask" />
      ),
      renderExpand: record => <ActionPlansTable serviceOrder={record} />
    },

    {
      label: t('listServiceOrders:table.responsible'),
      dataIndex: 'user',
      sortIndex: 'USER_NAME',
      render: (_, record) => <EditableServiceOrdersUser serviceOrder={record} />,
      flex: 3
    },

    ...appendIf(!hidePlace, {
      label: t('listServiceOrders:table.destination'),
      dataIndex: 'place',
      sortIndex: 'PLACE_NAME',
      flex: 4,
      render: (item, record) => {
        if (record.asset?.id)
          return (
            <AssetPresenter
              asset={record.asset}
              place={record.place}
              size="small"
              color={COLORS.dark3}
              hideIcon
              breakWord
            />
          )
        return <PlacePresenter place={item} size="small" color={COLORS.dark3} hideIcon breakWord />
      }
    }),

    {
      label: t('listServiceOrders:table.scheduling'),
      dataIndex: 'scheduling',
      sortIndex: 'SCHEDULING',
      flex: 2,
      size: 'xsmall',
      format: formatDate('DD/MM/YY HH:mm')
    },

    {
      label: t('listServiceOrders:table.completion'),
      dataIndex: 'endDate',
      sortIndex: 'END_DATE',
      flex: 2,
      size: 'xsmall',
      format: formatDate('DD/MM/YY HH:mm')
    },

    {
      label: t('listServiceOrders:table.sla'),
      flex: 2,
      dataIndex: 'slaExpiresAt',
      size: 'xsmall',
      sortIndex: 'SLA_EXPIRES_AT',
      format: formatDate('DD/MM/YY HH:mm'),
      render: (value, record) => {
        const slaColor = getSlaStatusColor(record.slaExpiresAt)
        return (
          <Badge color={value && slaColor}>
            <Text>{value}</Text>
          </Badge>
        )
      }
    },

    {
      label: t('listServiceOrders:table.status'),
      dataIndex: 'status',
      sortIndex: 'STATUS_ID',
      flex: 1.5,
      render: (value, record) => <StatusTag item={value} />
    },

    {
      label: t('listServiceOrders:table.priority'),
      dataIndex: ['priority', 'name'],
      sortIndex: 'PRIORITY_ID',
      flex: 1.5,
      render: (value, record) => <StatusTag item={record.priority} type={PRIORITY} />
    },

    {
      label: t('listServiceOrders:table.tag'),
      dataIndex: ['tag', 'name'],
      // Sem `sortIndex`, a coluna caía no `dataIndex` (['tag','name']) como campo de ordenação, que o back
      // não conhece e quebrava a listagem. Segue a convenção de status/prioridade (enum em maiúsculo).
      sortIndex: 'TAG_NAME',
      flex: 1.5,
      render: (value, record) => <EditableServiceOrdersTag serviceOrder={record} />
    },

    {
      label: t('listServiceOrders:table.openedAt'),
      dataIndex: 'insertedAt',
      sortIndex: false,
      flex: 1.5,
      size: 'xsmall',
      format: (value, record) => {
        if (STATUS_OPEN_AT.includes(record.status?.name)) {
          return fromNow(value, { breakpoints: { hour: 48 }, hidePrefix: true })
        }

        return moment(value).format('L LT')
      },
      render: (value, record) => {
        const color = getSemaphoreConfigColor(record.insertedAt, semaphoreConfig?.value)
        return (
          <Badge color={value && color}>
            <Text>{value}</Text>
          </Badge>
        )
      }
    },

    {
      label: t('actions', { ns: 'common' }),
      width: 2.5,
      render: useRowActionsColumn(useServiceOrdersActionsList)
    }
  ]

  return <DataTable rowKey="id" columns={columns} size="medium" data={data} inPanel {...props} />
}

export default ServiceOrdersTable
